// src/app/useCompteursMenu.js — les compteurs (pastilles) du menu.
// Déplacé à l'identique depuis src/App.jsx (P-3.5, squelette) : deux hooks,
// appelés là où vivait leur code, dans le même ordre.

import { supabase } from '../supabase'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { STATUS, normalizeStatus, fetchOrchestratorPoolIncidents } from '../lib/incidents-api'
import { compterSousSeuil } from '../ecrans/orchestrateur/stock-calculs'
import { computeNavBadges, withJourAlias } from '../lib/nav-badges'
import { useNotifCounters } from '../lib/use-notif-counters'
import { lirePorte } from '../ecrans/portes'

export function useCompteursMenu({ incidents, currentUser }) {
  // ADR-269 — compteurs de notification au niveau App (badges menus parents).
  // Communication : 2 count-queries head:true (mêmes requêtes / même cadence
  // 60 s que MessagesPanel historique) hissées ici pour que le badge vive SANS
  // que la section soit montée ; le hook est passé en prop à MessagesPanel
  // (source unique, zéro double polling). Admin uniquement.
  const notif = useNotifCounters(currentUser?.role === 'admin')
  // Opérations : dérivé du state `incidents` DÉJÀ chargé au niveau App (LOT
  // Incidents 13) — zéro fetch supplémentaire. Miroir des compteurs des
  // sous-onglets IncidentsTab (normalizeStatus). Fraîcheur = chargement de
  // session + refreshIncidents après chaque écriture admin.
  const incidentCounts = useMemo(() => {
    const c = { [STATUS.EN_COURS]: 0, [STATUS.A_RACHETER]: 0, [STATUS.RESOLU]: 0 }
    incidents.forEach(i => { c[normalizeStatus(i.status)]++ })
    return c
  }, [incidents])
  // Somme par catégorie parent (pur, trivial). Zéro = pas de pastille.
  // BADGE « À CONFIRMER » (couche confirmation, décision Dimitri 2026-08-18 :
  // le badge, pas la ligne de rapport) — propositions d'horaires en attente de
  // la confirmation admin. Même motif que les badges orchestrateur : endpoint
  // dédié (2 entiers, 0 montant, 0 PII), montage + 5 min. L'urgence (échéance
  // d'empreinte < 48 h) vire le badge du sous-onglet en terracotta « ! ».
  const [aConfirmer, setAConfirmer] = useState({ count: 0, urgent: 0 })
  const estAdminBadge = currentUser?.role === 'admin'
  useEffect(() => {
    if (!estAdminBadge) { setAConfirmer({ count: 0, urgent: 0 }); return }
    let annule = false
    const lire = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'services_hourly_confirm_count' }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (!annule) setAConfirmer({ count: Number(json?.count) || 0, urgent: Number(json?.urgent_count) || 0 })
      } catch (e) {
        // Un badge ne casse jamais l'app : sans compte, pas de pastille.
        console.error('[nav] badge à confirmer', e?.message || e)
      }
    }
    lire()
    const id = setInterval(lire, 5 * 60 * 1000)
    return () => { annule = true; clearInterval(id) }
  }, [estAdminBadge])

  // LOT 2 — le sous-onglet Incidents d'Aujourd'hui porte SA pastille (le
  // même compteur que le parent, sans les horaires à confirmer qui ont la
  // leur sur Horaires).
  const navBadgesIncidents = ((incidentCounts?.en_cours || 0) + (incidentCounts?.a_racheter || 0))
  // Pastille Calendrier (maquette NEST v677) : les fermetures non confirmées,
  // par la porte de sa tuile. Illisible = pas de pastille, comme les autres.
  const [fermeturesNonConfirmees, setFermeturesNonConfirmees] = useState(0)
  useEffect(() => {
    if (!estAdminBadge) { setFermeturesNonConfirmees(0); return undefined }
    let annule = false
    const lire = async () => {
      const r = await lirePorte('ecran_fermetures_lire')
      if (annule) return
      if (r.etat === 'ok') setFermeturesNonConfirmees(Number(r.data.nb) || 0)
      else console.error('[nav] badge fermetures', r.motif)
    }
    lire()
    const id = setInterval(lire, 5 * 60 * 1000)
    return () => { annule = true; clearInterval(id) }
  }, [estAdminBadge])
  const navBadgesAdmin = useMemo(
    () => computeNavBadges({ unreadInbox: notif.unreadInbox, pendingRequests: notif.pendingRequests, incidentCounts, aConfirmer: aConfirmer.count, fermeturesNonConfirmees }),
    [notif.unreadInbox, notif.pendingRequests, incidentCounts, aConfirmer.count, fermeturesNonConfirmees],
  )

  // BADGE ORCHESTRATEUR (lot apparence 2026-08-14) — sa file d'incidents À
  // JUGER (en_cours de son pool). La SEULE pièce non-CSS du lot : une lecture
  // via l'endpoint service_role existant (orchestrateur_incidents_pool,
  // cloisonné pool ∧ logIds serveur), zéro écriture, zéro garde touchée.
  // Rafraîchie au montage puis toutes les 5 min — un badge n'est pas un flux.
  // Stock rejoindra ce compte avec sa refonte (décision Dimitri).
  const [orchIncidentsAJuger, setOrchIncidentsAJuger] = useState(0)
  const estOrchestrateurBadge = currentUser?.role === 'orchestrateur'
  useEffect(() => {
    if (!estOrchestrateurBadge) { setOrchIncidentsAJuger(0); return }
    let annule = false
    const lire = async () => {
      try {
        const pool = await fetchOrchestratorPoolIncidents({ status: 'en_cours' })
        if (!annule) setOrchIncidentsAJuger(Array.isArray(pool) ? pool.length : 0)
      } catch (e) {
        // Un badge ne casse jamais l'app : sans compte, pas de pastille.
        console.error('[nav] badge incidents orchestrateur', e?.message || e)
      }
    }
    lire()
    const id = setInterval(lire, 5 * 60 * 1000)
    // C-a (tri Dimitri 2026-08-21) : une ACTION dans un écran (jugement,
    // geste, décision) rafraîchit la pastille TOUT DE SUITE via cet événement
    // — l'intervalle de 5 min ne reste qu'un filet.
    window.addEventListener('fbz:orchestrateur-badges', lire)
    return () => { annule = true; clearInterval(id); window.removeEventListener('fbz:orchestrateur-badges', lire) }
  }, [estOrchestrateurBadge])

  // BADGE PLANNING — LES MÉNAGES SANS AGENT (décision Dimitri 2026-08-22) :
  // « 10 ménages sans agent, c'est beaucoup, et rien ne le signale là où on
  // les traite. » Chaque badge annonce ce que SON onglet permet de faire : les
  // assignations se font au Planning, elles n'entrent pas dans « À décider ».
  // UNE SEULE VALEUR D'ÉTAT : cette lecture initialise le compte du jour
  // courant, et la vue Planning l'écrase avec le JOUR AFFICHÉ dès qu'elle est
  // montée (onSansAgentJour) — jamais deux compteurs qui divergent.
  const [orchSansAgentJour, setOrchSansAgentJour] = useState(0)
  useEffect(() => {
    if (!estOrchestrateurBadge) { setOrchSansAgentJour(0); return }
    let annule = false
    const lire = async () => {
      try {
        const jour = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
        const { data, error } = await supabase
          .from('menage_runs')
          .select('id')
          .is('assigned_provider_email', null)
          .in('status', ['scheduled', 'in_progress'])
          .eq('scheduled_date', jour)
        if (error) { console.error('[nav] badge sans agent', error.message || error); return }
        if (!annule) setOrchSansAgentJour(Array.isArray(data) ? data.length : 0)
      } catch (e) {
        console.error('[nav] badge sans agent', e?.message || e)
      }
    }
    lire()
    const id = setInterval(lire, 5 * 60 * 1000)
    window.addEventListener('fbz:orchestrateur-badges', lire)
    return () => { annule = true; clearInterval(id); window.removeEventListener('fbz:orchestrateur-badges', lire) }
  }, [estOrchestrateurBadge])

  // BADGE DEMANDES D'HORAIRES (T5 2026-08-15, demande Dimitri) : une demande en
  // attente n'allumait RIEN dans la barre. Même motif que le badge incidents —
  // l'endpoint liste existant (cloisonné serveur), lecture seule, 5 min. Le
  // payload ne porte AUCUN montant (strip du même jour). Hub éteint → 0.
  const [orchDemandesEnAttente, setOrchDemandesEnAttente] = useState(0)
  useEffect(() => {
    if (!estOrchestrateurBadge) { setOrchDemandesEnAttente(0); return }
    let annule = false
    const lire = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'services_orchestrateur_hourly_list' }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (!annule) setOrchDemandesEnAttente(Number(json?.pending_count) || 0)
      } catch (e) {
        console.error('[nav] badge demandes orchestrateur', e?.message || e)
      }
    }
    lire()
    const id = setInterval(lire, 5 * 60 * 1000)
    // C-a (tri Dimitri 2026-08-21) : une ACTION dans un écran (jugement,
    // geste, décision) rafraîchit la pastille TOUT DE SUITE via cet événement
    // — l'intervalle de 5 min ne reste qu'un filet.
    window.addEventListener('fbz:orchestrateur-badges', lire)
    return () => { annule = true; clearInterval(id); window.removeEventListener('fbz:orchestrateur-badges', lire) }
  }, [estOrchestrateurBadge])
  return {
    notif, aConfirmer, navBadgesIncidents, navBadgesAdmin,
    orchIncidentsAJuger, estOrchestrateurBadge, orchSansAgentJour, setOrchSansAgentJour,
    orchDemandesEnAttente,
  }
}

export const TONS_ADMIN = Object.freeze({
  accueil: '#7A2E6E', calendrier: '#7A2E6E', jour: '#7A2E6E', operations: '#7A2E6E', argent: '#7A2E6E',
  logements: '#7A2E6E', voyageurs: '#7A2E6E',
})

export function useBadgesGroupes({
  stockCentral, navBadgesAdmin, orchIncidentsAJuger, estOrchestrateurBadge, orchSansAgentJour,
  orchDemandesEnAttente, estControleur,
}) {
  // CONTRÔLES À TRAITER (nav 7→4, 2026-08-19) — même motif que les deux
  // compteurs ci-dessus : endpoint liste existant (cloisonné serveur), 5 min.
  const [orchControlesATraiter, setOrchControlesATraiter] = useState(0)
  useEffect(() => {
    if (!estOrchestrateurBadge || !estControleur) { setOrchControlesATraiter(0); return }
    let annule = false
    const lire = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'controle_a_controler' }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (!annule) setOrchControlesATraiter(Array.isArray(json?.menages) ? json.menages.length : 0)
      } catch (e) {
        console.error('[nav] badge contrôles orchestrateur', e?.message || e)
      }
    }
    lire()
    const id = setInterval(lire, 5 * 60 * 1000)
    // C-a : rafraîchissement immédiat sur action (même événement que les
    // autres pastilles orchestrateur).
    window.addEventListener('fbz:orchestrateur-badges', lire)
    return () => { annule = true; clearInterval(id); window.removeEventListener('fbz:orchestrateur-badges', lire) }
  }, [estOrchestrateurBadge, estControleur])

  // STOCK SOUS SEUIL — pastille de l'orchestrateur, dérivée du stock central
  // déjà chargé : aucun appel de plus. Même règle que sa vue (maquette, `<`).
  const stockSousSeuil = useMemo(() => compterSousSeuil(stockCentral), [stockCentral])

  // NAV 7→4 (2026-08-19) : chez l'orchestrateur les badges vivent sur les
  // GROUPES — « À décider » porte le TOTAL (demandes + incidents + contrôles),
  // « Stock » l'ambre du sous-seuil. Les compteurs par entrée nourrissent la
  // bascule, pas la barre.
  const navBadges = useMemo(
    () => (estOrchestrateurBadge
      ? {
          planning: orchSansAgentJour,
          // P-4.11 : six entrées (maquette v677), chacune porte SON compte.
          'menage-controle': orchControlesATraiter,
          incidents: orchIncidentsAJuger,
          'a-decider': orchDemandesEnAttente,
          stock: stockSousSeuil,
        }
      : withJourAlias(navBadgesAdmin)),
    [estOrchestrateurBadge, orchIncidentsAJuger, orchDemandesEnAttente, orchControlesATraiter, orchSansAgentJour, stockSousSeuil, navBadgesAdmin],
  )
  // La couleur du badge par groupe : terracotta pour ce qui attend une
  // décision, AMBRE pour le stock presque épuisé.
  const navBadgeTons = estOrchestrateurBadge
    // Maquette v677 : une action attend = aubergine, partout, jamais de rouge.
    ? { planning: '#7A2E6E', 'menage-controle': '#7A2E6E', incidents: '#7A2E6E', 'a-decider': '#7A2E6E', stock: '#7A2E6E' }
    // ADMIN : toutes ses familles (sinon repli DS.corail de NavBadge), même
    // aubergine que la barre du bas. Le JETON DS.corail n'est pas touché.
    : TONS_ADMIN
  return { orchControlesATraiter, navBadges, navBadgeTons }
}
